import React, { Fragment, useState, useEffect, useRef } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import { getClientImageSrc, getSowDocumentUrl } from "../../../services/clientApi";

const FILE_SIZE_LIMIT = 2 * 1024 * 1024; // 2 MB
const SOW_SIZE_LIMIT = 10 * 1024 * 1024; // 10 MB
const ALLOWED_TYPES = ["image/jpeg", "image/jpg", "image/png"];
const SOW_MIME = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const clientSchema = Yup.object().shape({
    clientName: Yup.string()
        .min(3, "Client Name must be at least 3 characters")
        .max(100, "Client Name must not exceed 100 characters")
        .required("Client Name is required"),
    contactPerson: Yup.string()
        .min(3, "Contact Person must be at least 3 characters")
        .max(100, "Contact Person must not exceed 100 characters"),
    clientEmail: Yup.string()
        .email("Enter a valid email address"),
    clientMobile: Yup.string()
        .matches(/^\d{10,12}$/, "Mobile number must be 10–12 digits"),
    clientImage: Yup.mixed()
        .nullable()
        .test("fileType", "Only JPG or PNG images are allowed", (value) => {
            if (!value) return true;
            return ALLOWED_TYPES.includes(value.type);
        })
        .test("fileSize", "Image must be smaller than 2 MB", (value) => {
            if (!value) return true;
            return value.size <= FILE_SIZE_LIMIT;
        }),
    sowDocument: Yup.mixed()
        .nullable()
        .test("sowType", "Only PDF or Word (.doc, .docx) are allowed", (value) => {
            if (!value) return true;
            if (SOW_MIME.includes(value.type)) return true;
            if (!value.type && value.name) {
                const ext = value.name.split(".").pop()?.toLowerCase();
                return ["pdf", "doc", "docx"].includes(ext);
            }
            return false;
        })
        .test("sowSize", "SOW document must be smaller than 10 MB", (value) => {
            if (!value) return true;
            return value.size <= SOW_SIZE_LIMIT;
        }),
});

const ClientForm = ({
    initialValues,
    onSubmit,
    onCancel,
    isEditMode,
    externalSubmitting,
}) => {
    const sowInputRef = useRef(null);
    const imageInputRef = useRef(null);

    const [imagePreview, setImagePreview] = useState(() =>
        getClientImageSrc(initialValues?.clientImage)
    );

    useEffect(() => {
        setImagePreview(getClientImageSrc(initialValues?.clientImage));
    }, [initialValues?.clientImage]);

    const defaultValues = {
        clientName: "",
        contactPerson: "",
        clientEmail: "",
        clientMobile: "",
        ...initialValues,
        clientImage: null, // always start file input as null
        sowDocument: null,
    };

    const handleImageChange = (e, setFieldValue, setFieldTouched) => {
        const file = e.currentTarget.files[0];
        setFieldTouched("clientImage", true);
        if (file) {
            setFieldValue("clientImage", file);
            const reader = new FileReader();
            reader.onload = () => setImagePreview(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const handleSowChange = (e, setFieldValue, setFieldTouched) => {
        const file = e.currentTarget.files?.[0];
        setFieldTouched("sowDocument", true);
        if (file) {
            setFieldValue("sowDocument", file);
        } else {
            setFieldValue("sowDocument", null);
        }
    };

    const fieldClass = (touched, error) =>
        `form-group mb-3 ${touched ? (error ? "is-invalid" : "is-valid") : ""}`;

    const errBlock = (touched, error) =>
        touched && error ? (
            <div
                className="invalid-feedback animated fadeInUp"
                style={{ display: "block" }}
            >
                {error}
            </div>
        ) : null;

    return (
        <Fragment>
            <Formik
                initialValues={defaultValues}
                enableReinitialize
                validationSchema={clientSchema}
                onSubmit={onSubmit}
            >
                {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    isSubmitting,
                    resetForm,
                    setFieldValue,
                    setFieldTouched,
                }) => (
                    <form onSubmit={handleSubmit}>
                        <div className="row">
                            {/* ── Left Column ── */}
                            <div className="col-xl-6 col-lg-6">

                                {/* Client Name */}
                                <div className={fieldClass(touched.clientName, errors.clientName)}>
                                    <label className="text-label">
                                        Client Name <span className="text-danger">*</span>
                                    </label>
                                    <div className="input-group">
                                        <span className="input-group-text">
                                            <i className="fa fa-building" />
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="clientName"
                                            placeholder="Enter client / company name"
                                            value={values.clientName}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                        />
                                        {errBlock(touched.clientName, errors.clientName)}
                                    </div>
                                </div>

                               

                                {/* Client Email */}
                                <div className={fieldClass(touched.clientEmail, errors.clientEmail)}>
                                    <label className="text-label">
                                        Client Email
                                    </label>
                                    <div className="input-group">
                                        <span className="input-group-text">
                                            <i className="fa fa-envelope" />
                                        </span>
                                        <input
                                            type="email"
                                            className="form-control"
                                            name="clientEmail"
                                            placeholder="Enter client email"
                                            value={values.clientEmail}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                        />
                                        {errBlock(touched.clientEmail, errors.clientEmail)}
                                    </div>
                                </div>

                                 {/* Client Image */}
                                <div className={fieldClass(touched.clientImage, errors.clientImage)}>
                                    <label className="text-label">
                                        Client Image{" "}
                                        <small className="text-muted">(JPG / PNG, max 2 MB — optional)</small>
                                    </label>
                                    <div className="input-group">
                                        <span className="input-group-text">
                                            <i className="fa fa-image" />
                                        </span>
                                        <input
                                            ref={imageInputRef}
                                            type="file"
                                            className="form-control"
                                            accept="image/jpeg,image/jpg,image/png"
                                            onChange={(e) =>
                                                handleImageChange(e, setFieldValue, setFieldTouched)
                                            }
                                        />
                                        {errBlock(touched.clientImage, errors.clientImage)}
                                    </div>

                                    {/* Image Preview */}
                                    {imagePreview && (
                                        <div className="mt-2 d-flex align-items-center gap-3">
                                            <img
                                                src={getClientImageSrc(imagePreview) || imagePreview}
                                                alt="Preview"
                                                style={{
                                                    width: 80,
                                                    height: 80,
                                                    objectFit: "cover",
                                                    borderRadius: "8px",
                                                    border: "2px solid #dee2e6",
                                                }}
                                            />
                                            <div>
                                                <p className="mb-1 text-muted" style={{ fontSize: 12 }}>
                                                    Image preview
                                                </p>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => {
                                                        setFieldValue("clientImage", null);
                                                        setImagePreview(
                                                            getClientImageSrc(initialValues?.clientImage)
                                                        );
                                                        if (imageInputRef.current) imageInputRef.current.value = "";
                                                    }}
                                                >
                                                    <i className="fa fa-times me-1" /> Remove
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>

                            </div>

                            {/* ── Right Column ── */}
                            <div className="col-xl-6 col-lg-6">
 {/* Contact Person */}
                                <div className={fieldClass(touched.contactPerson, errors.contactPerson)}>
                                    <label className="text-label">
                                        Contact Person
                                    </label>
                                    <div className="input-group">
                                        <span className="input-group-text">
                                            <i className="fa fa-user" />
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="contactPerson"
                                            placeholder="Enter contact person name"
                                            value={values.contactPerson}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                        />
                                        {errBlock(touched.contactPerson, errors.contactPerson)}
                                    </div>
                                </div>
                                {/* Client Mobile */}
                                <div className={fieldClass(touched.clientMobile, errors.clientMobile)}>
                                    <label className="text-label">
                                        Client Mobile
                                    </label>
                                    <div className="input-group">
                                        <span className="input-group-text">
                                            <i className="fa fa-phone" />
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="clientMobile"
                                            placeholder="10–12 digit mobile number"
                                            value={values.clientMobile}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            maxLength={12}
                                        />
                                        {errBlock(touched.clientMobile, errors.clientMobile)}
                                    </div>
                                </div>

                               

                                {/* SOW document */}
                                <div className={fieldClass(touched.sowDocument, errors.sowDocument)}>
                                    <label className="text-label">
                                        SOW document{" "}
                                        <small className="text-muted">
                                            (PDF / Word, max 10 MB — optional)
                                        </small>
                                    </label>
                                    <div className="input-group">
                                        <span className="input-group-text">
                                            <i className="fa fa-file-text-o" />
                                        </span>
                                        <input
                                            ref={sowInputRef}
                                            type="file"
                                            className="form-control"
                                            accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                            onChange={(e) =>
                                                handleSowChange(e, setFieldValue, setFieldTouched)
                                            }
                                        />
                                        {errBlock(touched.sowDocument, errors.sowDocument)}
                                    </div>
                                    {values.sowDocument instanceof File && (
                                        <div className="mt-2 d-flex align-items-center gap-2 flex-wrap">
                                            <span className="text-muted small">
                                                <i className="fa fa-paperclip me-1" />
                                                {values.sowDocument.name}
                                            </span>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-danger"
                                                onClick={() => {
                                                    setFieldValue("sowDocument", null);
                                                    if (sowInputRef.current) sowInputRef.current.value = "";
                                                }}
                                            >
                                                <i className="fa fa-times me-1" /> Remove
                                            </button>
                                        </div>
                                    )}
                                    {!values.sowDocument &&
                                        initialValues?.sowDocument &&
                                        getSowDocumentUrl(initialValues.sowDocument) && (
                                            <div className="mt-2">
                                                <a
                                                    href={getSowDocumentUrl(initialValues.sowDocument)}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn btn-sm btn-outline-primary"
                                                >
                                                    <i className="fa fa-external-link me-1" />
                                                    View current SOW
                                                </a>
                                            </div>
                                        )}
                                </div>

                                {/* Action Buttons */}
                                <div className="form-group mb-0 mt-4 d-flex gap-2 flex-wrap">
                                    <button
                                        type="submit"
                                        className="btn btn-primary py-2"
                                        disabled={isSubmitting || externalSubmitting}
                                    >
                                        {isSubmitting || externalSubmitting ? (
                                            <>
                                                <span
                                                    className="spinner-border spinner-border-sm me-2"
                                                    role="status"
                                                    aria-hidden="true"
                                                />
                                                {isEditMode ? "Updating..." : "Saving..."}
                                            </>
                                        ) : isEditMode ? (
                                            "Update Client"
                                        ) : (
                                            "Add Client"
                                        )}
                                    </button>
                                    {!isEditMode && (
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary py-2"
                                            onClick={() => {
                                                resetForm();
                                                setImagePreview(getClientImageSrc(initialValues?.clientImage));
                                                if (sowInputRef.current) sowInputRef.current.value = "";
                                                if (imageInputRef.current) imageInputRef.current.value = "";
                                            }}
                                        >
                                            Reset
                                        </button>
                                    )}
                                    {onCancel && (
                                        <button
                                            type="button"
                                            className="btn btn-light py-2"
                                            onClick={onCancel}
                                        >
                                            Cancel
                                        </button>
                                    )}
                                </div>

                            </div>
                        </div>
                    </form>
                )}
            </Formik>
        </Fragment>
    );
};

export default ClientForm;
